import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

interface BudgetRequest {
  id: string;
  user_id: string;
  amount: number;
  status: string;
  admin_notes: string | null;
  created_at: string;
  profiles?: {
    email: string | null;
    phone: string | null;
  } | null;
}

export const AdminBudgetRequests = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [requests, setRequests] = useState<BudgetRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<BudgetRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      fetchRequests();
    }
  }, [isAdmin]);

  const checkAdminStatus = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .single();

    setIsAdmin(!!data && !error);
    setLoading(false);
  };

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from("budget_purchase_requests")
        .select(`
          *,
          profiles (
            email,
            phone
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      console.log("Budget requests data:", data); // Debug log
      setRequests(data || []);
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error(t("toast.requestFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request: BudgetRequest) => {
    setProcessing(request.id);
    try {
      // Update request status
      const { error: requestError } = await supabase
        .from("budget_purchase_requests")
        .update({
          status: "approved",
          admin_notes: adminNotes
        })
        .eq("id", request.id);

      if (requestError) throw requestError;

      // Update user's budget
      const { data: profile, error: profileFetchError } = await supabase
        .from("profiles")
        .select("budget")
        .eq("id", request.user_id)
        .single();

      if (profileFetchError) throw profileFetchError;

      const newBudget = Number(profile.budget) + Number(request.amount);

      const { error: budgetError } = await supabase
        .from("profiles")
        .update({ budget: newBudget })
        .eq("id", request.user_id);

      if (budgetError) throw budgetError;

      toast.success(t("admin.requestApproved") + ` $${request.amount}`);
      setSelectedRequest(null);
      setAdminNotes("");
      fetchRequests();
    } catch (error: any) {
      console.error("Error approving request:", error);
      toast.error(t("toast.requestFailed"));
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (request: BudgetRequest) => {
    setProcessing(request.id);
    try {
      const { error } = await supabase
        .from("budget_purchase_requests")
        .update({
          status: "rejected",
          admin_notes: adminNotes
        })
        .eq("id", request.id);

      if (error) throw error;

      toast.success(t("admin.requestRejected"));
      setSelectedRequest(null);
      setAdminNotes("");
      fetchRequests();
    } catch (error: any) {
      console.error("Error rejecting request:", error);
      toast.error(t("toast.requestFailed"));
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.accessDenied")}</CardTitle>
          <CardDescription>{t("admin.noPrivileges")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.budgetRequests")}</CardTitle>
          <CardDescription>{t("admin.reviewRequests")}</CardDescription>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{t("admin.noRequests")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("admin.date")}</TableHead>
                  <TableHead>{t("admin.user")}</TableHead>
                  <TableHead>{t("admin.amount")}</TableHead>
                  <TableHead>{t("admin.status")}</TableHead>
                  <TableHead>{t("admin.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      {new Date(request.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {request.profiles?.email && (
                          <span className="text-sm">{request.profiles.email}</span>
                        )}
                        {request.profiles?.phone && (
                          <span className="text-xs text-muted-foreground dir-ltr">{request.profiles.phone}</span>
                        )}
                        {!request.profiles?.email && !request.profiles?.phone && (
                          <span className="text-sm text-muted-foreground">{t("admin.unknownUser")}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>${request.amount}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          request.status === "approved"
                            ? "default"
                            : request.status === "rejected"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {request.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {request.status === "pending" && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => {
                              setSelectedRequest(request);
                              setAdminNotes("");
                            }}
                            disabled={processing === request.id}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            {t("admin.approve")}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setSelectedRequest(request);
                              setAdminNotes("");
                            }}
                            disabled={processing === request.id}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            {t("admin.reject")}
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedRequest && `${t("admin.reviewRequest")} - $${selectedRequest.amount}`}
            </DialogTitle>
            <DialogDescription>
              {t("admin.addNotes")}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={t("admin.notesPlaceholder")}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedRequest(null)}>
              {t("admin.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedRequest && handleReject(selectedRequest)}
              disabled={!!processing}
            >
              {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("admin.reject")}
            </Button>
            <Button
              onClick={() => selectedRequest && handleApprove(selectedRequest)}
              disabled={!!processing}
            >
              {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("admin.approve")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
